import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram } from 'lucide-react';
import { companyInfo } from '../mockData';
import { COMPANY_LOGO_SRC } from '../constants/branding';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [socialLinks, setSocialLinks] = useState({
    twitter: '',
    instagram: '',
    linkedin: '',
    email: '',
    maps_url: ''
  });
  const [sectionNavigation, setSectionNavigation] = useState({
    services: [],
    industries: []
  });

  useEffect(() => {
    fetchSocialLinks();
    fetchSectionNavigation();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      if (response.data.links) {
        setSocialLinks(response.data.links);
      }
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const fetchSectionNavigation = async () => {
    try {
      const response = await axios.get(`${API}/section-navigation`);
      setSectionNavigation({
        services: response?.data?.services || [],
        industries: response?.data?.industries || []
      });
    } catch (error) {
      console.error('Error fetching section navigation:', error);
    }
  };

  const scrollToSectionByHash = (hash) => {
    if (!hash) return;
    const target = document.getElementById(hash);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.classList.add('ring-4', 'ring-[#1e3a8a]/40', 'ring-offset-2', 'transition-all', 'duration-700');
    window.setTimeout(() => {
      target.classList.remove('ring-4', 'ring-[#1e3a8a]/40', 'ring-offset-2', 'transition-all', 'duration-700');
    }, 1800);
  };

  const handleSectionNavigation = (event, path) => {
    const [targetPath, rawHash] = path.split('#');
    const hash = rawHash ? decodeURIComponent(rawHash) : '';

    // If already on target page, force smooth-scroll immediately
    // so repeated clicks on same hash still work.
    if (location.pathname === targetPath && hash) {
      event.preventDefault();
      if (location.hash !== `#${hash}`) {
        navigate(`${targetPath}#${hash}`, { replace: true });
      }
      scrollToSectionByHash(hash);
    }
  };

  const handleLogoClick = (event) => {
    event.preventDefault();
    navigate('/');

    // Ensure scroll reset also works when user is already on "/".
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleQuickLinkClick = (event, path) => {
    // If already on the target route, prevent a no-op navigation.
    if (location.pathname === path) {
      event.preventDefault();
    }

    navigate(path);

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  return (
    <footer aria-label="Site footer" className="bg-[#1e3a8a] text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="min-w-0 flex flex-col items-center text-center lg:items-start lg:text-left">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="mb-4 inline-flex max-w-full shrink-0 justify-center rounded-md bg-white/95 px-3 py-2 shadow-sm ring-1 ring-white/20"
            >
              <img
                src={COMPANY_LOGO_SRC}
                alt="PFA AND ASSOCIATES LLP"
                className="h-9 w-auto max-h-9 max-w-[min(100%,280px)] shrink-0 object-contain object-center sm:h-10 sm:max-h-10"
                decoding="async"
              />
            </Link>
            <p className="text-sm text-gray-300 mb-4 max-w-md">
              {companyInfo.tagline}
            </p>
            <div className="flex flex-wrap justify-center gap-4 lg:justify-start">
              {socialLinks.linkedin && (
                <a 
                  href={socialLinks.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <Linkedin size={20} />
                </a>
              )}
              {socialLinks.twitter && (
                <a 
                  href={socialLinks.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <Twitter size={20} />
                </a>
              )}
              {socialLinks.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <Instagram size={20} />
                </a>
              )}
              {socialLinks.email && (
                <a 
                  href={`mailto:${socialLinks.email}`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <Mail size={20} />
                </a>
              )}
              {socialLinks.maps_url && (
                <a 
                  href={socialLinks.maps_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <MapPin size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" onClick={(event) => handleQuickLinkClick(event, '/about')} className="text-sm text-gray-300 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" onClick={(event) => handleQuickLinkClick(event, '/services')} className="text-sm text-gray-300 hover:text-white transition-colors">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" onClick={(event) => handleQuickLinkClick(event, '/practice-areas')} className="text-sm text-gray-300 hover:text-white transition-colors">
                  Industries Serve
                </Link>
              </li>
              <li>
                <Link to="/resources" onClick={(event) => handleQuickLinkClick(event, '/resources')} className="text-sm text-gray-300 hover:text-white transition-colors">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" onClick={(event) => handleQuickLinkClick(event, '/contact')} className="text-sm text-gray-300 hover:text-white transition-colors">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {sectionNavigation.services.length > 0 ? (
                sectionNavigation.services.map((service) => (
                  <li key={service.id || service.anchor}>
                    <Link
                      to={service.path || `/services#${service.anchor}`}
                      onClick={(event) => handleSectionNavigation(event, service.path || `/services#${service.anchor}`)}
                      className="text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-sm text-gray-300">No services available</li>
              )}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Industries Serve</h3>
            <ul className="space-y-2">
              {sectionNavigation.industries.length > 0 ? (
                sectionNavigation.industries.map((industry) => (
                  <li key={industry.id || industry.anchor}>
                    <Link
                      to={industry.path || `/practice-areas#${industry.anchor}`}
                      onClick={(event) => handleSectionNavigation(event, industry.path || `/practice-areas#${industry.anchor}`)}
                      className="text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      {industry.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-sm text-gray-300">No industries available</li>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/nee6vUWTRFst96qQ7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                >
                  {companyInfo.address}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={18} className="flex-shrink-0" />
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={18} className="flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                >
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-gray-700 pt-8 text-center">
          <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-5">
            <p className="text-sm text-gray-300">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
